/**
 * Yerel ComfyUI (LTX-2.3) istemcisi.
 *
 * ComfyUI kullanicinin kendi makinesinde calisir (varsayilan 127.0.0.1:8188).
 * Panel Cloudflare Pages'te barinsa da istekler tarayicidan dogrudan localhost'a
 * gider; Railway'deki API bu adrese erisemez, o yuzden bu katman bilerek
 * tamamen istemci tarafindadir.
 */

const URL_KEY = "erc-comfyui-url";
export const DEFAULT_COMFY_URL = "http://127.0.0.1:8188";

export function getComfyUrl(): string {
  try {
    return localStorage.getItem(URL_KEY) || DEFAULT_COMFY_URL;
  } catch {
    return DEFAULT_COMFY_URL;
  }
}

export function setComfyUrl(url: string) {
  try {
    localStorage.setItem(URL_KEY, url.replace(/\/+$/, ""));
  } catch {
    /* localStorage kapali olabilir */
  }
}

/** ComfyUI'nin cikti klasoru (diskte). Uretilen dosyanin yolu kopyalanip
 *  Etsy'nin dosya secme penceresine dogrudan yapistirilabilsin diye. */
const OUTPUT_DIR_KEY = "erc-comfyui-output-dir";

export function getOutputDir(): string {
  try { return localStorage.getItem(OUTPUT_DIR_KEY) || ""; } catch { return ""; }
}

export function setOutputDir(dir: string) {
  try {
    const v = dir.trim().replace(/^"|"$/g, "").replace(/[\\/]+$/, "");
    if (v) localStorage.setItem(OUTPUT_DIR_KEY, v); else localStorage.removeItem(OUTPUT_DIR_KEY);
  } catch { /* localStorage kapali olabilir */ }
}

/** system_stats argv'sinden cikti klasorunu tahmin et: --output-directory
 *  verilmisse o, main.py mutlak yolsa yanindaki output. Goreli yolda bulunamaz. */
function outputDirFromArgv(argv: unknown): string | null {
  if (!Array.isArray(argv)) return null;
  const a = argv.map(String);
  const i = a.indexOf("--output-directory");
  if (i >= 0 && a[i + 1]) return a[i + 1].replace(/[\\/]+$/, "");
  const main = a[0] ?? "";
  if (!/^([a-zA-Z]:[\\/]|\/)/.test(main)) return null;
  const sep = main.includes("\\") ? "\\" : "/";
  return `${main.replace(/[\\/][^\\/]*$/, "")}${sep}output`;
}

/** Ciktinin diskteki tam yolu (klasor bilinmiyorsa null) */
export function localPathFor(o: { filename: string; subfolder?: string }, dir = getOutputDir()): string | null {
  if (!dir) return null;
  const sep = dir.includes("/") && !dir.includes("\\") ? "/" : "\\";
  return [dir, ...(o.subfolder ? o.subfolder.split(/[\\/]/) : []), o.filename].filter(Boolean).join(sep);
}

/** /view?filename=&subfolder= adresinden dosya bilgisini geri cikar (gecmis kayitlari icin) */
export function parseOutputUrl(url: string): { filename: string; subfolder: string } | null {
  try {
    const u = new URL(url);
    const filename = u.searchParams.get("filename");
    return filename ? { filename, subfolder: u.searchParams.get("subfolder") ?? "" } : null;
  } catch { return null; }
}

export interface ComfyStats {
  ramTotalGb: number;
  ramFreeGb: number;
  vramTotalGb: number;
  vramFreeGb: number;
  deviceName: string;
  version: string;
  /** ComfyUI'nin bildirdigi cikti klasoru (bulunamazsa null) */
  outputDir: string | null;
}

export async function checkComfy(signal?: AbortSignal): Promise<ComfyStats> {
  const res = await fetch(`${getComfyUrl()}/system_stats`, { signal });
  if (!res.ok) throw new Error(`ComfyUI yanıt vermedi (${res.status})`);
  const d = await res.json();
  const dev = d.devices?.[0] ?? {};
  const gb = (n: number) => Math.round(((n ?? 0) / 1024 ** 3) * 10) / 10;
  return {
    ramTotalGb: gb(d.system?.ram_total),
    ramFreeGb: gb(d.system?.ram_free),
    vramTotalGb: gb(dev.vram_total),
    vramFreeGb: gb(dev.vram_free),
    deviceName: dev.name ?? "bilinmiyor",
    version: d.system?.comfyui_version ?? "?",
    outputDir: outputDirFromArgv(d.system?.argv),
  };
}

export interface ComfyImageRef {
  name: string;
  subfolder: string;
  type: string;
}

/** Gorseli ComfyUI'nin input klasorune yukler; LoadImage bu adi kullanir. */
export async function uploadImage(file: File | Blob, filename?: string): Promise<ComfyImageRef> {
  const fd = new FormData();
  const name = filename ?? (file instanceof File ? file.name : "erc-input.png");
  fd.append("image", file, name);
  fd.append("overwrite", "true");
  const res = await fetch(`${getComfyUrl()}/upload/image`, { method: "POST", body: fd });
  if (!res.ok) throw new Error(`Görsel yüklenemedi (${res.status})`);
  const d = await res.json();
  return { name: d.name, subfolder: d.subfolder ?? "", type: d.type ?? "input" };
}

export type ApiWorkflow = Record<string, { class_type: string; inputs: Record<string, unknown> }>;

export interface GenerateParams {
  imageName: string;
  prompt: string;
  negativePrompt?: string;
  width: number;
  height: number;
  frames: number;
  seed?: number;
  /** Varsayilan sessiz. LTX sesi videoyla birlikte uretir, biz sadece muxlamayiz. */
  withAudio?: boolean;
  /** Kaynak gorsele baglilik (0-1). Yuksek = sadik ama az hareket. */
  strength?: number;
  /** Kare hizi; sure = frames / fps. */
  fps?: number;
  /**
   * LTXVPreprocess.img_compression. Dusuk deger kaynagi daha net korur,
   * yuksek deger modele daha fazla serbestlik verir.
   */
  imgCompression?: number;
}

function findByClass(wf: ApiWorkflow, cls: string): string[] {
  return Object.keys(wf).filter((id) => wf[id]?.class_type === cls);
}

/** [nodeId, slot] seklindeki bir baglantidan kaynak dugum id'sini cikarir. */
function linkSource(v: unknown): string | null {
  return Array.isArray(v) && typeof v[0] === "string" ? v[0] : null;
}

/**
 * Bir dugum girdisine deger yazar.
 *
 * API export'unda Primitive dugumleri inline edilmez; ornegin kare sayisi
 * EmptyLTXVLatentVideo.length uzerinde ["4988", 0] baglantisi olarak durur.
 * Boyle bir durumda degeri kaynak Primitive'in `value` alanina yazar,
 * degilse girdiye dogrudan yazar.
 */
function setResolved(wf: ApiWorkflow, nodeId: string, key: string, value: unknown) {
  const node = wf[nodeId];
  if (!node || !(key in node.inputs)) return;
  const src = linkSource(node.inputs[key]);
  if (src && wf[src] && "value" in wf[src].inputs) wf[src].inputs.value = value;
  else if (!src) node.inputs[key] = value;
}

/**
 * API formatindaki workflow sablonunu parametrelerle doldurur.
 * Sablonu mutasyona ugratmaz; derin kopya dondurur.
 *
 * Dugumleri id ile degil class_type ile bulur, boylece sablon yeniden
 * export edilip id'ler degistiginde bozulmaz.
 */
export function buildPrompt(template: ApiWorkflow, p: GenerateParams): ApiWorkflow {
  const wf: ApiWorkflow = JSON.parse(JSON.stringify(template));

  const loadImage = findByClass(wf, "LoadImage")[0];
  if (!loadImage) throw new Error("Şablonda LoadImage düğümü yok");
  wf[loadImage].inputs.image = p.imageName;

  // Gorselden video: i2v bypass'i kapat (sablonda metinden-video icin acik gelir)
  for (const id of findByClass(wf, "LTXVImgToVideoConditionOnly")) {
    setResolved(wf, id, "bypass", false);
    // Sablonda iki asama var: ilki kaynak gorsele baglilik (0.7), ikincisi
    // buyutme asamasinda tam baglilik (1). Sadece ilkini ayarla.
    const cur = wf[id].inputs.strength;
    if (p.strength != null && typeof cur === "number" && cur < 1) {
      wf[id].inputs.strength = p.strength;
    }
  }

  if (p.imgCompression != null) {
    for (const id of findByClass(wf, "LTXVPreprocess")) {
      wf[id].inputs.img_compression = p.imgCompression;
    }
  }

  const latent = findByClass(wf, "EmptyLTXVLatentVideo")[0];
  if (!latent) throw new Error("Şablonda EmptyLTXVLatentVideo düğümü yok");
  wf[latent].inputs.width = p.width;
  wf[latent].inputs.height = p.height;
  setResolved(wf, latent, "length", p.frames);

  // Ses latent'i kare sayisiyla ayni uzunlukta olmali. Sablonda genelde ayni
  // Primitive'e baglidir, o zaman bu cagri zararsizca ayni degeri yazar.
  for (const id of findByClass(wf, "LTXVEmptyLatentAudio")) {
    setResolved(wf, id, "frames_number", p.frames);
  }

  // Pozitif/negatif metni, LTXVConditioning'in hangi dugumden beslendigine
  // bakarak ayirt et - iki CLIPTextEncode'u sirasina guvenerek ayirmak kirilgan.
  const cond = findByClass(wf, "LTXVConditioning")[0];
  if (cond) {
    // fps tek bir Primitive'den hem kosullamaya hem CreateVideo'ya gider
    if (p.fps != null) setResolved(wf, cond, "frame_rate", p.fps);
    const posId = linkSource(wf[cond].inputs.positive);
    const negId = linkSource(wf[cond].inputs.negative);
    if (posId && wf[posId]) wf[posId].inputs.text = p.prompt;
    if (negId && wf[negId] && p.negativePrompt != null) wf[negId].inputs.text = p.negativePrompt;
  }

  // Sessiz video: CreateVideo'nun opsiyonel 'audio' girdisini kaldirmak yeterli.
  // Ses latent'i yine uretilir ama muxlanmaz; ses dalini sokmek grafigi bozar.
  if (!p.withAudio) {
    for (const id of findByClass(wf, "CreateVideo")) {
      delete wf[id].inputs.audio;
    }
  }

  const seed = p.seed ?? Math.floor(Math.random() * 2 ** 31);
  findByClass(wf, "RandomNoise").forEach((id, i) => {
    // Iki asamada farkli seed kullanilir; ayni degeri vermek desen tekrarina yol acar
    wf[id].inputs.noise_seed = seed + i;
  });

  return wf;
}

export interface QueuedJob {
  promptId: string;
  clientId: string;
}

export async function queuePrompt(wf: ApiWorkflow): Promise<QueuedJob> {
  const clientId = crypto.randomUUID();
  const res = await fetch(`${getComfyUrl()}/prompt`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ prompt: wf, client_id: clientId }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Kuyruğa alınamadı (${res.status}): ${text.slice(0, 400)}`);
  }
  const d = await res.json();
  if (d.error) throw new Error(d.error.message ?? "ComfyUI isteği reddetti");
  return { promptId: d.prompt_id, clientId };
}

export interface VideoOutput {
  filename: string;
  subfolder: string;
  type: string;
  url: string;
}

export function outputUrl(o: { filename: string; subfolder?: string; type?: string }): string {
  const q = new URLSearchParams({
    filename: o.filename,
    subfolder: o.subfolder ?? "",
    type: o.type ?? "output",
  });
  return `${getComfyUrl()}/view?${q}`;
}

/** Tamamlanmis bir isin urettigi videolari gecmisten okur. */
export async function getOutputs(promptId: string): Promise<VideoOutput[]> {
  const res = await fetch(`${getComfyUrl()}/history/${promptId}`);
  if (!res.ok) throw new Error(`Geçmiş okunamadı (${res.status})`);
  const hist = await res.json();
  const entry = hist[promptId];
  if (!entry) return [];
  const out: VideoOutput[] = [];
  for (const nodeOut of Object.values(entry.outputs ?? {}) as any[]) {
    // ComfyUI surumune gore video ciktilari 'images', 'gifs' veya 'videos' altinda gelir
    for (const key of ["videos", "gifs", "images"]) {
      for (const f of nodeOut?.[key] ?? []) {
        if (!/\.(mp4|webm|mov)$/i.test(f.filename)) continue;
        out.push({
          filename: f.filename,
          subfolder: f.subfolder ?? "",
          type: f.type ?? "output",
          url: outputUrl(f),
        });
      }
    }
  }
  return out;
}

export interface Progress {
  /** 0-1 arasi; bilinmiyorsa null */
  ratio: number | null;
  label: string;
}

/**
 * Isin ilerlemesini WebSocket ile izler, bitince ciktilari dondurur.
 * WebSocket kurulamazsa gecmisi yoklamaya duser.
 */
export function watchJob(
  job: QueuedJob,
  onProgress: (p: Progress) => void,
  signal?: AbortSignal,
): Promise<VideoOutput[]> {
  const base = getComfyUrl().replace(/^http/, "ws");
  return new Promise((resolve, reject) => {
    let settled = false;
    let ws: WebSocket | null = null;
    let poll: number | null = null;

    const finish = async (ok: boolean, err?: Error) => {
      if (settled) return;
      settled = true;
      ws?.close();
      if (poll) window.clearInterval(poll);
      if (!ok) return reject(err);
      try {
        resolve(await getOutputs(job.promptId));
      } catch (e) {
        reject(e as Error);
      }
    };

    signal?.addEventListener("abort", () => finish(false, new Error("İptal edildi")));

    // WebSocket kopsa bile is arka planda devam eder; gecmisi yoklamak guvenli ag.
    poll = window.setInterval(async () => {
      try {
        const outs = await getOutputs(job.promptId);
        if (outs.length) finish(true);
      } catch {
        /* henuz bitmemis olabilir */
      }
    }, 5000);

    try {
      ws = new WebSocket(`${base}/ws?clientId=${job.clientId}`);
    } catch {
      onProgress({ ratio: null, label: "WebSocket kurulamadı, yoklamaya geçildi" });
      return;
    }

    ws.onmessage = (ev) => {
      if (typeof ev.data !== "string") return; // ikili onizleme kareleri
      let msg: any;
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }
      if (msg.data?.prompt_id && msg.data.prompt_id !== job.promptId) return;

      if (msg.type === "progress") {
        const { value, max } = msg.data ?? {};
        onProgress({ ratio: max ? value / max : null, label: `Adım ${value}/${max}` });
      } else if (msg.type === "execution_error") {
        finish(false, new Error(msg.data?.exception_message ?? "Üretim hatası"));
      } else if (msg.type === "executing") {
        if (msg.data?.node === null) finish(true);
        else if (msg.data?.node) onProgress({ ratio: null, label: `Çalışıyor: ${msg.data.node}` });
      }
    };

    ws.onerror = () => onProgress({ ratio: null, label: "Bağlantı sorunu, yoklamaya geçildi" });
  });
}
